import { Outlet, useLocation } from 'react-router-dom';
import { ProSkeleton } from '@ant-design/pro-components';
import { DatePicker, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import PieECharts from './pieCharts';
import BarCount from './barCount';
import BarPT from './barPT';
import LineOMS from './lineOMS';
import dayjs from '@/utils/dayjsConfig';
import { useState, useEffect, useMemo } from 'react';
import { waitTime, getTimeToString } from '@/utils/toolt';
import { TABLEPAI, PageEnum } from '@/enums/pageEnum';
import { BasicTable, HView } from '@/components';
import { Alarms, Faults, Passenger, Lifts, DownSvg } from '@/assets';

export default function Home() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  const getData = async () => {
    await waitTime(1000);
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const lfRecord = useMemo(() => {
    return (
      <Card className="mt-4">
        <div className="text-[16px] font-semibold mb-4">
          {t('lastRecord')}
        </div>
        <BasicTable
          isShowCheckbox={false}
          columns={[
            {
              title: t('setTime'),
              dataIndex: 'alarmStartTs',
              width: 150,
              renderText: (text: number) => <>{getTimeToString(text)}</>,
            },
            { title: t('lift'), dataIndex: 'liftName', width: 150 },
            {
              title: t('liftFaultNumber'),
              dataIndex: 'alarmLiftFaultNumber',
              width: 150,
              align: 'center' as const,
            },
            {
              title: t('complaintCount'),
              dataIndex: 'complaintCnt',
              width: 200,
              align: 'center' as const,
              renderText: (text: number) => (
                <>
                  {text ? (
                    text
                  ) : (
                    <span className="text-[#BFBFBF]">&mdash;</span>
                  )}
                </>
              ),
            },
            {
              title: t('action'),
              dataIndex: 'action',
              align: 'right' as const,
              renderText: (_: string, record: { id: string }) => (
                <HView
                  name={`${t('view')} \u2192`}
                  path={`${PageEnum.ALARMLF}/detail/${record?.id}`}
                  state={record}
                />
              ),
            },
          ]}
          size={3}
          isDefaultToolBar={false}
          isPagination={false}
          className="min-h-max !p-0 dashboardTable"
          requestUrl={TABLEPAI.LIFT_FAULT}
        />
      </Card>
    );
  }, [t]);

  const lastAlarmRecord = useMemo(() => {
    return (
      <Card className="mt-4">
        <div className="text-[16px] font-semibold mb-4">
          {t('lastAlarmRecord')}
        </div>
        <BasicTable
          isShowCheckbox={false}
          columns={[
            {
              title: t('setTime'),
              dataIndex: 'startTs',
              width: 150,
              renderText: (text: number) => <>{getTimeToString(text)}</>,
            },
            {
              title: t('lift'),
              dataIndex: 'lift.name',
              width: 150,
              renderText: (
                _: string,
                record: { lift: { name: string } },
              ) => record.lift?.name,
            },
            { title: t('errorCode'), dataIndex: 'type', width: 150 },
            { title: t('description'), dataIndex: 'description', width: 200 },
            {
              title: t('action'),
              dataIndex: 'action',
              align: 'right' as const,
              renderText: (
                _: string,
                record: { lift: { id: string } },
              ) => (
                <HView
                  name={`${t('view')} \u2192`}
                  path={`${PageEnum.LIFT}/detail/${record?.lift?.id}`}
                  state={record}
                />
              ),
            },
          ]}
          size={3}
          isDefaultToolBar={false}
          isPagination={false}
          className="min-h-max !p-0 dashboardTable"
          requestUrl={TABLEPAI.ALARM_RBE}
        />
      </Card>
    );
  }, [t]);

  if (loading) {
    return (
      <ProSkeleton
        type="descriptions"
        pageHeader={false}
        active={false}
      />
    );
  }

  if (pathname !== PageEnum.BASE_HOME) return <Outlet />;

  return (
    <div className="p-4 homeIconDiv">
      {/* KPI Cards */}
      <div className="flex items-stretch justify-between gap-4 text-[#202224]">
        <Card className="flex-auto">
          <div className="flex items-center justify-between min-h-[136px]">
            <div>
              <p className="text-[16px]">{t('liftAlarms')}</p>
              <p className="text-[28px] font-bold">21</p>
              <span className="text-[12px] text-[#52C41A] flex items-center gap-1">
                <img src={DownSvg} alt="" className="w-[12px]" /> 12%
              </span>
            </div>
            <div>
              <img src={Alarms} alt="" srcSet="" />
            </div>
          </div>
        </Card>
        <Card className="flex-auto">
          <div className="min-h-[136px]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[16px]">{t('liftFaults')}</p>
                <p className="text-[28px] font-bold">12</p>
                <span className="text-[12px] text-[#52C41A] flex items-center gap-1">
                  <img src={DownSvg} alt="" className="w-[12px]" /> 8%
                </span>
              </div>
              <div>
                <p className="text-[16px]">{t('breakdown')}</p>
                <p className="text-[28px] font-bold">2</p>
              </div>
              <div>
                <img src={Faults} alt="" srcSet="" />
              </div>
            </div>
            <div className="border-t pt-2 mt-3 dark:border-[#ffffff21] flex items-center justify-between">
              <div>
                <span className="text-[#545454] text-[12px]">
                  {t('dispatched')}{' '}
                </span>
                <b className="text-[13px]">10</b>
              </div>
              <div>
                <span className="text-[#545454] text-[12px]">
                  {t('pending')}{' '}
                </span>
                <b className="text-[13px]">2</b>
              </div>
            </div>
          </div>
        </Card>
        <Card className="flex-auto">
          <div className="flex items-center justify-between min-h-[136px]">
            <div>
              <p className="text-[16px]">{t('passengerTrap')}</p>
              <p className="text-[28px] font-bold">0</p>
            </div>
            <div>
              <img src={Passenger} alt="" srcSet="" />
            </div>
          </div>
        </Card>
        <Card className="flex-auto">
          <div className="flex items-center justify-between min-h-[136px]">
            <div>
              <p className="text-[16px]">{t('lifts')}</p>
              <p className="text-[28px] font-bold">340</p>
            </div>
            <div>
              <img src={Lifts} alt="" srcSet="" />
            </div>
          </div>
        </Card>
      </div>

      {/* Real API tables — DO NOT TOUCH */}
      {lfRecord}
      {lastAlarmRecord}

      {/* Fault & Alarm Trends */}
      <Card className="mt-4">
        <div className="flex justify-between items-center mb-3">
          <div className="text-[16px] font-bold">
            {t('faultAlarmTrends')}
          </div>
          <DatePicker.RangePicker
            style={{ width: 230 }}
            maxDate={dayjs(new Date())}
            placeholder={['Start date', 'End date']}
          />
        </div>
        <BarCount data={[]} />
      </Card>

      {/* Passenger Traps */}
      <Card className="mt-4">
        <div className="flex justify-between items-center mb-3">
          <div className="text-[16px] font-bold">
            {t('passengerTraps')}
          </div>
          <DatePicker.RangePicker
            style={{ width: 230 }}
            maxDate={dayjs(new Date())}
            placeholder={['Start date', 'End date']}
          />
        </div>
        <div className="flex justify-between gap-8">
          <div>
            <PieECharts data={[]} />
          </div>
          <div className="flex-1">
            <BarPT data={[]} />
          </div>
        </div>
      </Card>

      {/* OMS Schedule */}
      <Card className="mt-4">
        <div className="flex justify-between pt-3 gap-8">
          <div className="w-[250px]">
            <div className="text-[16px] font-bold pb-12">
              OMS {t('schedule')}{' '}
            </div>
            <div className="flex justify-start mb-20">
              <div className="flex-1">
                <p className="text-[16px] font-medium text-[#545454]">
                  {t('all')}
                </p>
                <p className="text-[28px] font-bold">340</p>
              </div>
              <div className="flex-1">
                <p className="text-[16px] font-medium text-[#545454]">
                  {t('arranged')}
                </p>
                <p className="text-[28px] font-bold">285</p>
              </div>
            </div>
            <div className="flex justify-start">
              <div className="flex-1">
                <p className="text-[16px] font-medium text-[#545454]">
                  {t('processing')}
                </p>
                <p className="text-[28px] font-bold">42</p>
              </div>
              <div className="flex-1">
                <p className="text-[16px] font-medium text-[#545454]">
                  {t('delayed')}
                </p>
                <p className="text-[28px] font-bold">13</p>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="text-right">
              <DatePicker.RangePicker
                style={{ width: 230 }}
                maxDate={dayjs(new Date())}
                placeholder={['Start date', 'End date']}
              />
            </div>
            <LineOMS data={[]} />
          </div>
        </div>
      </Card>
    </div>
  );
}
